import {
  FormEventHandler,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "Data/Store/hooks";
import { selectCartTotalQuantity } from "Data/Cart/CartSelectors";
import { CartService } from "../../CartService";
import { UseNavbarParams } from "@model/NavBar.model";

export function useNavbar({ cartCountProp }: UseNavbarParams) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [serverCount, setServerCount] = useState(0);

  const menuPanelRef = useRef<HTMLDivElement | null>(null);

  //sticky refs/state
  const headerRef = useRef<HTMLElement | null>(null);
  const [navHeight, setNavHeight] = useState(0);
  const [isSticky, setIsSticky] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const guestTotalQty = useAppSelector(selectCartTotalQuantity);
  const authToken = useAppSelector(
    (s) => s.userAuthenticationReducer?.token?.trim() ?? ""
  );
  const isLoggedIn = !!authToken;

  const cartService = useMemo(() => new CartService(), []);
  const closeMenu = () => setOpen(false);

  //  Close when route changes
  useEffect(() => {
    if (!open) return;
    setOpen(false);
  }, [location.pathname]); // keep this focused only on route changes

  //  Close when clicking outside panel OR pressing Escape
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      const panel = menuPanelRef.current;
      if (!panel) return;

      const target = event.target as Node | null;
      if (target && !panel.contains(target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

useLayoutEffect(() => {
  const navbarElement = headerRef.current;
  if (!navbarElement) return;

  const updateNavbarHeight = () => {
    setNavHeight(navbarElement.getBoundingClientRect().height);
  };

  updateNavbarHeight();

  const resizeObserver = new ResizeObserver(updateNavbarHeight);
  resizeObserver.observe(navbarElement);

  return () => resizeObserver.disconnect();
}, []);


  //  become sticky only after scrolling past its height
  useEffect(() => {
    const onScroll = () => {
      const shouldStick = window.scrollY > navHeight;
      setIsSticky(shouldStick);
    };

    onScroll();

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [navHeight]);

  // Search submit logic
  const submitSearch: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    const trimmed = q.trim();
    closeMenu();
    if (!trimmed) {
      navigate("/products");
      return;
    }
    navigate({
      pathname: "/products",
      search: `?q=${encodeURIComponent(trimmed)}`,
    });
  };

  // Subscribe to server cart count when logged in
  useEffect(() => {
    if (!isLoggedIn) return;
    return cartService.subscribeCartCount({
      token: authToken,
      onChange: setServerCount,
    });
  }, [isLoggedIn, authToken, cartService]);

  // Badge count logic
  const badgeCount = isLoggedIn ? serverCount : guestTotalQty ?? cartCountProp;

  return {
    q,
    setQ,
    open,
    setOpen,
    closeMenu,
    menuPanelRef,
    submitSearch,
    badgeCount,

    headerRef,
    navHeight,
    isSticky,
  };
}
